import React, { useMemo, useState } from 'react';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import PostStatusBadge from './PostStatusBadge';
import PlatformBadge from './PlatformBadge';
import PostEmptyState from './PostEmptyStates';
import { formatPstDisplay, formatPstShort, getPstDayKey, formatPstDate } from './SchedulePicker';
import { POST_STATUSES, TERMINAL_STATUSES } from '../../services/SocialPostService';

function getSnippet(content, maxLen = 100) {
  if (!content) return '';
  const text = content.replace(/\n+/g, ' ').trim();
  return text.length > maxLen ? text.slice(0, maxLen) + '...' : text;
}

function groupHistoryByDay(posts) {
  const groups = {};
  posts.forEach((post) => {
    const ts = post.published_at || post.canceled_at || post.updated_at;
    const key = ts ? getPstDayKey(ts) : 'unknown';
    if (!groups[key]) groups[key] = { key, date: ts, posts: [] };
    groups[key].posts.push(post);
  });
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
}

export default function PostHistoryList({
  posts = [],
  onClick,
  loading = false,
}) {
  const [showAll, setShowAll] = useState(false);

  const historyPosts = useMemo(() => {
    return posts
      .filter((p) => TERMINAL_STATUSES.has(p.status) || p.status === POST_STATUSES.failed)
      .sort((a, b) => {
        const tsA = a.published_at || a.canceled_at || a.updated_at;
        const tsB = b.published_at || b.canceled_at || b.updated_at;
        return new Date(tsB) - new Date(tsA);
      });
  }, [posts]);

  const displayPosts = showAll ? historyPosts : historyPosts.slice(0, 20);
  const dayGroups = useMemo(() => groupHistoryByDay(displayPosts), [displayPosts]);

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="animate-pulse h-16 bg-zinc-100 rounded-xl" />
        ))}
      </div>
    );
  }

  if (historyPosts.length === 0) {
    return <PostEmptyState type="history" />;
  }

  return (
    <div className="space-y-6">
      {dayGroups.map((group) => (
        <div key={group.key}>
          <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-3">
            {group.date ? formatPstDate(group.date) : 'Unknown date'}
          </h4>
          <div className="space-y-2">
            {group.posts.map((post) => {
              const timestamp = post.published_at || post.canceled_at || post.updated_at;
              return (
                <div
                  key={post._id}
                  onClick={() => onClick?.(post)}
                  className="flex items-center gap-4 px-4 py-3 rounded-xl bg-white border border-zinc-200 hover:border-zinc-300 hover:shadow-sm cursor-pointer transition-all group"
                >
                  {/* Status indicator */}
                  <div className={`
                    w-2 h-2 rounded-full flex-shrink-0
                    ${post.status === POST_STATUSES.posted ? 'bg-emerald-500' :
                      post.status === POST_STATUSES.canceled ? 'bg-zinc-400' :
                      'bg-red-500'}
                  `} />

                  {/* Content snippet */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-zinc-700 truncate">
                      {getSnippet(post.content, 80) || <span className="italic text-zinc-400">Empty post</span>}
                    </p>
                  </div>

                  {/* Badges */}
                  <PlatformBadge platform={post.platform} size="xs" showLabel={false} className="flex-shrink-0" />
                  <PostStatusBadge status={post.status} size="xs" className="flex-shrink-0" />

                  {/* Timestamp */}
                  <span className="text-xs text-zinc-400 flex-shrink-0 whitespace-nowrap">
                    {timestamp ? formatPstShort(timestamp) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Show more */}
      {historyPosts.length > 20 && !showAll && (
        <div className="text-center pt-2">
          <button
            onClick={() => setShowAll(true)}
            className="flex items-center gap-1 mx-auto px-4 py-2 rounded-xl text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors"
          >
            <FiChevronDown className="w-4 h-4" />
            Show all {historyPosts.length} posts
          </button>
        </div>
      )}

      {showAll && historyPosts.length > 20 && (
        <div className="text-center pt-2">
          <button
            onClick={() => setShowAll(false)}
            className="flex items-center gap-1 mx-auto px-4 py-2 rounded-xl text-sm font-medium text-zinc-500 hover:bg-zinc-100 transition-colors"
          >
            <FiChevronUp className="w-4 h-4" />
            Show less
          </button>
        </div>
      )}

      <div className="text-xs text-zinc-400 text-center">
        {historyPosts.length} total post{historyPosts.length !== 1 ? 's' : ''} in history
      </div>
    </div>
  );
}
